/**
 * Alluvion Home — Print Your Own Photo Upload & Cart (v2)
 * ========================================================
 * Handles photo upload to Cloudflare R2 and adds to cart with the photo URL
 * as a line item property. Dawn handles variant selection natively.
 *
 * Changes from v1:
 *   - Reads the selected size/frame from dropdowns OR pills/swatches
 *   - Add to cart now requires a photo (v1 let orders through without one)
 *   - Upload/cart errors no longer leave the button stuck on "Uploading…"
 *   - Keeps Dawn's button markup intact (v1 wiped the inner <span>)
 *   - Respects the quantity selector
 *   - TIFF / HEIC files that the browser can't display are still accepted
 *   - Reuses the uploaded file if the customer adds the same photo twice
 *   - Inline messages instead of browser alert() pop-ups
 *
 * Only runs on: /products/print-your-own-photo
 *
 * INSTALLATION — same as before. Load order in layout/theme.liquid:
 *
 *   {% if request.path contains '/products/print-your-own-photo' %}
 *     <script src="{{ 'alluvion-preview.js' | asset_url }}" defer></script>
 *     <script src="{{ 'alluvion-frame-picker.js' | asset_url }}" defer></script>
 *     <script src="{{ 'alluvion-configurator.js' | asset_url }}" defer></script>
 *   {% endif %}
 *
 * The upload zone markup in sections/main-product.liquid keeps the same IDs.
 * Suggested new wording for the idle state:
 *   <span>Upload your photo</span>
 *   <span class="alv-upload-sub">Straight from your phone or computer &middot; JPG, PNG or TIFF</span>
 *
 * Keep the product JSON tag near the end of the product info section:
 *   <script type="application/json" id="alv-product-json">{{ product | json }}</script>
 */

(function () {
  'use strict';

  if (!window.location.pathname.includes('/products/print-your-own-photo')) return;

  /* ── Cloudflare Worker URL ────────────────────────────────────────── */
  var WORKER_URL = 'https://alluvion-upload.elliot-389.workers.dev';

  /* ── Minimum pixels on shortest side per print size ──────────────── */
  var MIN_PX     = { A6: 620, A5: 874, A4: 1240, A3: 1748, A2: 2480, A1: 3508, A0: 4964 };
  var SIZE_ORDER = ['A5', 'A4', 'A3', 'A2', 'A1', 'A0'];

  /* With a mount, the photo prints one A-size smaller than the finished size */
  var SMALLER = { A0: 'A1', A1: 'A2', A2: 'A3', A3: 'A4', A4: 'A5', A5: 'A6' };

  var OK_EXT = /\.(jpe?g|png|tiff?|heic|heif|webp)$/i;

  /* ── State ────────────────────────────────────────────────────────── */
  var productData     = null;
  var imgW = 0, imgH  = 0;
  var canMeasure      = false;   // browser could read the image dimensions
  var photoFile       = null;
  var photoPreviewUrl = null;    // object URL for on-page previews
  var uploadedUrl     = null;    // R2 URL for the current file
  var isBusy          = false;

  /* ── Boot ─────────────────────────────────────────────────────────── */
  function init() {
    var jsonEl = document.getElementById('alv-product-json');
    if (jsonEl) {
      try { productData = JSON.parse(jsonEl.textContent); } catch (e) {
        console.warn('[Alluvion] Could not parse product JSON.', e);
      }
    }
    injectStyles();
    ensureMessageEl();
    bindUploadEvents();
    interceptAddToCart();
    bindSizeChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Read Dawn's options (dropdowns or pills/swatches) ───────────── */
  function getOptionValue(name) {
    var scope = document.querySelector('variant-selects, variant-radios') || document;
    var i, list = scope.querySelectorAll('select');
    for (i = 0; i < list.length; i++) {
      if ((list[i].name || '').indexOf(name) !== -1) return list[i].value;
    }
    list = scope.querySelectorAll('input[type="radio"]:checked');
    for (i = 0; i < list.length; i++) {
      if ((list[i].name || '').indexOf(name) !== -1) return list[i].value;
    }
    return null;
  }

  function toSizeKey(v) {
    var m = (v || '').match(/A\d/);
    return m ? m[0] : null;
  }

  function getSelectedSize() {
    return toSizeKey(getOptionValue('Finished Size'));
  }

  function isMounted() {
    var f = getOptionValue('Framing');
    return !!f && / With Mount$/.test(f);
  }

  /* The size the photo itself prints at, for a given finished size */
  function printSizeFor(finished, mounted) {
    return mounted ? SMALLER[finished] : finished;
  }

  /* ── Find the selected Shopify variant ───────────────────────────── */
  function findVariant() {
    if (productData && productData.variants && productData.options) {
      var wanted = productData.options.map(function (opt) {
        return getOptionValue(typeof opt === 'object' ? opt.name : opt);
      });
      if (wanted.every(Boolean)) {
        var match = productData.variants.find(function (v) {
          return wanted.every(function (val, i) { return v['option' + (i + 1)] === val; });
        });
        if (match) return match;
      }
    }
    /* Fallback: Dawn's hidden variant id field */
    var idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return idInput && idInput.value ? { id: Number(idInput.value) } : null;
  }

  function getQuantity() {
    var q = document.querySelector('.product-form__quantity input[name="quantity"]') ||
            document.querySelector('quantity-input input[name="quantity"]');
    var n = q ? parseInt(q.value, 10) : 1;
    return n > 0 ? n : 1;
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ── Re-check resolution when the size or mount changes ──────────── */
  function bindSizeChange() {
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (el && el.name && (el.name.indexOf('Finished Size') !== -1 || el.name.indexOf('Framing') !== -1)) {
        checkResolution(getSelectedSize());
      }
    });
  }

  /* ── Intercept Add to Cart ───────────────────────────────────────── */
  function interceptAddToCart() {
    /* Delegated + capture phase: still works if Dawn re-renders the button,
       and runs before Dawn's own handler. Also catches the sticky bar,
       which clicks the real button. */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.product-form__submit');
      if (!btn || btn.closest('quick-add-modal')) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      if (isBusy || btn.disabled) return;

      if (!photoFile) {
        showMessage('Upload your photo to add it to your cart.', 'error');
        var zone = document.getElementById('alv-upload-zone');
        if (zone) zone.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      addToCart(btn);
    }, true);
  }

  function addToCart(btn) {
    var variant = findVariant();
    if (!variant) {
      showMessage('Choose a size and frame to continue.', 'error');
      return;
    }
    hideMessage();

    /* Change only the label text so Dawn's button markup stays intact */
    var labelEl  = btn.querySelector('span') || btn;
    var original = labelEl.textContent;
    isBusy = true;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    function setLabel(t) { labelEl.textContent = t; }
    function done() {
      isBusy = false;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      setLabel(original);
    }

    var upload = uploadedUrl
      ? Promise.resolve(uploadedUrl)
      : uploadPhoto(photoFile, function (pct) { setLabel('Uploading photo… ' + pct + '%'); });

    upload
      .then(function (url) {
        uploadedUrl = url;
        setLabel('Adding to cart…');
        return addLine(variant.id, url);
      })
      .then(function (cart) {
        done();
        renderCart(cart);
      })
      .catch(function (err) {
        console.error('[Alluvion]', err);
        done();
        showMessage((err && err.userMessage) || 'Your photo couldn\'t be added. Try again.', 'error');
      });
  }

  function userError(msg, detail) {
    var e = new Error(detail || msg);
    e.userMessage = msg;
    return e;
  }

  /* ── Upload to the Cloudflare Worker ─────────────────────────────── */
  function uploadPhoto(file, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      var formData = new FormData();
      formData.append('file', file);

      xhr.upload.addEventListener('progress', function (ev) {
        if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      xhr.addEventListener('load', function () {
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (e) { /* not JSON */ }
        if (xhr.status >= 200 && xhr.status < 300 && data && data.url) {
          resolve(data.url);
        } else {
          reject(userError('Your photo didn\'t upload. Try again, or email info@alluvionhome.com if it keeps happening.',
            'Upload failed: ' + xhr.status + ' ' + ((data && data.error) || '')));
        }
      });
      xhr.addEventListener('error', function () {
        reject(userError('Your photo didn\'t upload. Check your connection and try again.', 'Network error'));
      });
      xhr.addEventListener('timeout', function () {
        reject(userError('The upload took too long. Check your connection and try again.', 'Timeout'));
      });
      xhr.addEventListener('abort', function () {
        reject(userError('The upload was cancelled. Try again.', 'Aborted'));
      });

      xhr.open('POST', WORKER_URL, true);
      xhr.timeout = 180000;
      xhr.send(formData);
    });
  }

  /* ── Add the line to the cart ────────────────────────────────────── */
  function addLine(variantId, url) {
    var drawer = document.querySelector('cart-drawer');
    var sections = drawer && typeof drawer.getSectionsToRender === 'function'
      ? drawer.getSectionsToRender().map(function (s) { return s.id; })
      : ['cart-drawer', 'cart-icon-bubble'];

    return fetch(cartRoot() + 'cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id:       variantId,
          quantity: getQuantity(),
          properties: {
            'Photo':     url,
            '_Filename': photoFile ? photoFile.name : 'Uploaded photo'
          }
        }],
        sections:     sections,
        sections_url: window.location.pathname
      })
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.status) {
          throw userError(data.description || 'This couldn\'t be added to your cart. Try again.', data.message);
        }
        return data;
      });
    });
  }

  function renderCart(cart) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.classList.remove('is-empty');
      drawer.renderContents(cart);
    } else {
      window.location.href = cartRoot() + 'cart';
    }
  }

  /* ── Photo upload handlers ───────────────────────────────────────── */
  function isImage(file) {
    return !!file && ((file.type && file.type.indexOf('image/') === 0) || OK_EXT.test(file.name || ''));
  }

  function bindUploadEvents() {
    var fileInput = document.getElementById('alv-file-input');
    var zone      = document.getElementById('alv-upload-zone');
    var removeBtn = document.getElementById('alv-remove-btn');
    if (!fileInput || !zone) return;

    zone.setAttribute('role', 'button');
    zone.setAttribute('tabindex', '0');
    zone.setAttribute('aria-label', 'Upload your photo');

    zone.addEventListener('click', function (e) {
      if (removeBtn && (e.target === removeBtn || removeBtn.contains(e.target))) return;
      fileInput.click();
    });
    zone.addEventListener('keydown', function (e) {
      if (e.target !== zone) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
    });

    fileInput.addEventListener('change', function (e) {
      if (e.target.files[0]) handleFile(e.target.files[0]);
    });

    zone.addEventListener('dragover', function (e) {
      e.preventDefault();
      zone.classList.add('alv-upload-zone--dragover');
    });
    zone.addEventListener('dragleave', function () {
      zone.classList.remove('alv-upload-zone--dragover');
    });
    zone.addEventListener('drop', function (e) {
      e.preventDefault();
      zone.classList.remove('alv-upload-zone--dragover');
      var file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        clearPhoto();
      });
    }
  }

  function handleFile(file) {
    if (!isImage(file)) {
      showMessage('That file isn\'t a photo. Choose a JPG, PNG or TIFF.', 'error');
      return;
    }
    hideMessage();
    photoFile   = file;
    uploadedUrl = null;
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    photoPreviewUrl = URL.createObjectURL(file);

    var img = new Image();
    img.onload = function () {
      imgW = img.naturalWidth;
      imgH = img.naturalHeight;
      canMeasure = true;
      showUploadThumb(photoPreviewUrl, file.name);
      checkResolution(getSelectedSize());
      document.dispatchEvent(new CustomEvent('alv:photo-changed', {
        detail: { dataUrl: photoPreviewUrl, orientation: imgW >= imgH ? 'landscape' : 'portrait' }
      }));
    };
    img.onerror = function () {
      /* e.g. TIFF in Chrome, HEIC on desktop: can't display it, but we can still print it */
      imgW = imgH = 0;
      canMeasure = false;
      showUploadThumb(null, file.name);
      checkResolution(null);
      showMessage('Photo added. Your browser can\'t preview this file type, but our designer checks every photo before printing.', 'info');
    };
    img.src = photoPreviewUrl;
  }

  function showUploadThumb(src, name) {
    var zone    = document.getElementById('alv-upload-zone');
    var idle    = document.getElementById('alv-upload-idle');
    var overlay = document.getElementById('alv-upload-overlay');
    var remove  = document.getElementById('alv-remove-btn');
    if (!zone) return;

    var preview  = zone.querySelector('.alv-preview');
    var fileName = zone.querySelector('.alv-file-name');
    if (src) {
      if (fileName) fileName.remove();
      if (!preview) {
        preview = document.createElement('img');
        preview.className = 'alv-preview';
        preview.alt = 'Your uploaded photo';
        zone.insertBefore(preview, zone.firstChild);
      }
      preview.src = src;
    } else {
      if (preview) preview.remove();
      if (!fileName) {
        fileName = document.createElement('span');
        fileName.className = 'alv-file-name';
        zone.insertBefore(fileName, zone.firstChild);
      }
      fileName.textContent = name || 'Photo added';
    }
    zone.classList.add('alv-upload-zone--has-image');
    if (idle)    idle.style.display    = 'none';
    if (overlay) overlay.style.display = src ? 'flex' : 'none';
    if (remove)  remove.style.display  = 'inline-block';
  }

  function clearPhoto() {
    var zone    = document.getElementById('alv-upload-zone');
    var idle    = document.getElementById('alv-upload-idle');
    var overlay = document.getElementById('alv-upload-overlay');
    var remove  = document.getElementById('alv-remove-btn');
    var input   = document.getElementById('alv-file-input');
    if (!zone) return;

    var preview  = zone.querySelector('.alv-preview');
    var fileName = zone.querySelector('.alv-file-name');
    if (preview)  preview.remove();
    if (fileName) fileName.remove();
    zone.classList.remove('alv-upload-zone--has-image');
    if (idle)    idle.style.display    = '';
    if (overlay) overlay.style.display = 'none';
    if (remove)  remove.style.display  = 'none';
    if (input)   input.value           = '';

    photoFile   = null;
    uploadedUrl = null;
    canMeasure  = false;
    imgW = imgH = 0;
    hideMessage();
    checkResolution(null);

    document.dispatchEvent(new CustomEvent('alv:photo-cleared'));
    if (photoPreviewUrl) { URL.revokeObjectURL(photoPreviewUrl); photoPreviewUrl = null; }
  }

  /* ── Resolution check with a size suggestion ─────────────────────── */
  /* `size` is the finished size; with a mount the photo prints one size smaller */
  function checkResolution(size) {
    var warn = document.getElementById('alv-res-warning');
    if (!warn) return;

    var mounted = isMounted();
    var print   = size ? printSizeFor(size, mounted) : null;
    if (!canMeasure || !print || !MIN_PX[print]) { warn.style.display = 'none'; return; }

    var shortSide = Math.min(imgW, imgH);
    if (shortSide >= MIN_PX[print]) { warn.style.display = 'none'; return; }

    /* Largest finished size that would print sharply with the current mount choice */
    var best = null;
    SIZE_ORDER.forEach(function (s) {
      var p = printSizeFor(s, mounted);
      if (p && MIN_PX[p] && shortSide >= MIN_PX[p]) best = s;
    });

    warn.textContent = '\u26A0 Your photo may print slightly soft at ' + print +
      (mounted ? ' (inside your ' + size + ' mount). ' : '. ') +
      (best
        ? 'For the sharpest result, choose ' + best + ' or smaller.'
        : 'Use the original file rather than a screenshot or a copy sent by messaging app.');
    warn.style.display = 'block';
  }

  /* ── Inline messages ─────────────────────────────────────────────── */
  function ensureMessageEl() {
    if (document.getElementById('alv-upload-msg')) return;
    var zone = document.getElementById('alv-upload-zone');
    if (!zone || !zone.parentNode) return;
    var p = document.createElement('p');
    p.id = 'alv-upload-msg';
    p.className = 'alv-msg';
    p.setAttribute('aria-live', 'polite');
    p.style.display = 'none';
    zone.parentNode.insertBefore(p, zone.nextSibling);
  }

  function showMessage(text, type) {
    var el = document.getElementById('alv-upload-msg');
    if (!el) { if (type === 'error') window.alert(text); return; }
    el.textContent = text;
    el.className = 'alv-msg alv-msg--' + (type || 'info');
    el.style.display = 'block';
  }

  function hideMessage() {
    var el = document.getElementById('alv-upload-msg');
    if (el) el.style.display = 'none';
  }

  /* ── Scoped CSS ──────────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById('alv-styles')) return;
    var style = document.createElement('style');
    style.id  = 'alv-styles';
    style.textContent = [
      '#alv-upload-section { margin-bottom: 2.4rem; }',
      '.alv-label { font-size: 1.3rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; margin: 0 0 .8rem; color: rgb(var(--color-foreground)); }',
      '.alv-upload-zone { position: relative; overflow: hidden; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 8.8rem; border: 1px dashed rgba(var(--color-foreground),.3); border-radius: var(--buttons-radius,4px); background: rgba(var(--color-foreground),.03); cursor: pointer; transition: background .15s, border-color .15s; }',
      '.alv-upload-zone:hover, .alv-upload-zone--dragover { background: rgba(var(--color-foreground),.07); border-color: rgba(var(--color-foreground),.55); }',
      '.alv-upload-zone:focus-visible { outline: 2px solid rgb(var(--color-foreground)); outline-offset: 2px; }',
      '#alv-file-input { display: none; }',
      '.alv-upload-idle { display: grid; grid-template-columns: auto auto; align-items: center; column-gap: 1.2rem; row-gap: .2rem; color: rgba(var(--color-foreground),.65); font-size: 1.3rem; line-height: 1.3; pointer-events: none; text-align: left; padding: 0 1.2rem; }',
      '.alv-upload-idle > svg { grid-row: span 2; }',
      '.alv-upload-sub { font-size: 1.1rem; color: rgba(var(--color-foreground),.38); }',
      '.alv-preview { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }',
      '.alv-file-name { padding: 0 4rem; font-size: 1.3rem; text-align: center; word-break: break-all; color: rgb(var(--color-foreground)); }',
      '.alv-upload-overlay { position: absolute; inset: 0; background: rgba(0,0,0,.45); flex-direction: row; align-items: center; justify-content: center; gap: .5rem; color: #fff; font-size: 1.3rem; cursor: pointer; }',
      '.alv-upload-zone--has-image:hover .alv-upload-overlay { display: flex !important; }',
      '.alv-remove-btn { position: absolute; top: 8px; right: 8px; z-index: 2; background: rgb(var(--color-background)); border: 1px solid rgba(var(--color-foreground),.2); border-radius: var(--buttons-radius,4px); padding: 4px 10px; font-size: 1.1rem; font-family: inherit; cursor: pointer; color: rgb(var(--color-foreground)); }',
      '.alv-res-warning { margin-top: .6rem; font-size: 1.2rem; color: #b45309; }',
      '.alv-msg { margin: .8rem 0 0; font-size: 1.3rem; line-height: 1.4; }',
      '.alv-msg--error { color: #b42318; }',
      '.alv-msg--info { color: rgba(var(--color-foreground), .75); }'
    ].join('\n');
    document.head.appendChild(style);
  }

})();